import { useQuery } from '@tanstack/react-query';
import { message, Select, Spin } from 'antd';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import { fetchAPI } from '../../utils/api';

const CustomProductSuppliers = (props: {
  onChange: (value: number) => void;
  defaultValue?: number | string;
}) => {
  const [filters, setFilters] = useState({
    q: '',
    page: 1,
  });
  const [selectedValue, setSelectedValue] = useState<
    { value: number | string; label: string } | undefined
  >(
    props.defaultValue != null
      ? { value: Number(props.defaultValue), label: `[${props.defaultValue}]` }
      : undefined
  );
  const hasUserSelected = useRef(false);
  const defaultSupplierQuery = useQuery(
    ['supplier-by-id', props.defaultValue],
    async () => {
      if (!props.defaultValue) return null;
      try {
        const resp = await fetchAPI('suppliers/list-lite', {
          method: 'POST',
          payload: { ids: [Number(props.defaultValue)], per_page: 1 },
        });
        return resp?.items?.[0] || null;
      } catch {
        return null;
      }
    },
    { enabled: !!props.defaultValue, staleTime: 5 * 60 * 1000 }
  );

  const suppliers = useQuery(
    ['supplier-for-filter', filters],
    async () => {
      try {
        const resp = await fetchAPI('suppliers/lite', {
          payload: { ...filters, per_page: 50 },
        });
        return resp;
      } catch (error) {
        message.error('Failed get suppliers');
        return [];
      }
    },
    {
      placeholderData: {
        items: [],
        page: filters.page,
        per_page: 50,
        total: 0,
      },
      staleTime: 5 * 60 * 1000,
    }
  );
  // Resolve initial label from main data or dedicated default-supplier query
  useEffect(() => {
    if (!hasUserSelected.current && props.defaultValue != null) {
      const id = Number(props.defaultValue);
      const fromMain = suppliers.data?.items?.find(s => s.id === id);
      const fromDefault = defaultSupplierQuery.data;
      const found = fromMain || fromDefault;
      if (found) {
        setSelectedValue({
          value: found.id,
          label: `[${found.id}][${found.country}] ${found.name}`,
        });
      }
    }
  }, [suppliers.data, defaultSupplierQuery.data, props.defaultValue]);

  const supplierSearch = _.debounce(
    value => setFilters({ q: value, page: 1 }),
    500
  );

  return (
    <Select
      style={{ width: '100%' }}
      placeholder="Select Supplier"
      showSearch
      filterOption={false}
      labelInValue
      value={selectedValue}
      onSearch={value => {
        supplierSearch.cancel();
        supplierSearch(value);
      }}
      loading={suppliers.isLoading}
      notFoundContent={suppliers.isFetching ? <Spin size="small" /> : null}
      onChange={(value: { value: number | string; label: string }) => {
        if (value.value === 'prev') {
          setFilters(fil => ({ ...fil, page: fil.page - 1 }));
        } else if (value.value === 'next') {
          setFilters(fil => ({ ...fil, page: fil.page + 1 }));
        } else {
          hasUserSelected.current = true;
          setSelectedValue(value as { value: number; label: string });
          props.onChange(Number(value.value));
        }
      }}
    >
      {filters.page > 1 && (
        <Select.Option value="prev">{'<< Prev'}</Select.Option>
      )}
      {(suppliers.data?.items || []).map(supp => {
        const text = `[${supp.id}][${supp.country}] ${supp.name}`;
        return (
          <Select.Option value={supp.id} key={supp.id}>
            <span title={text}>{text}</span>
          </Select.Option>
        );
      })}
      {filters.page * (suppliers.data?.per_page || 50) <
        (suppliers.data?.total || 0) && (
        <Select.Option value="next" style={{ textAlign: 'right' }}>
          {'Next >>'}
        </Select.Option>
      )}
    </Select>
  );
};

export default CustomProductSuppliers;
